import React, {useEffect, useState, useRef} from 'react';
import { Box, Typography, IconButton, Divider, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '@fontsource/noto-sans-thai';
import WavesIcon from '@material-ui/icons/Waves';
// import {
//   LineChart, Line, XAxis, YAxis, CartesianGrid,
//   Tooltip, Legend, ResponsiveContainer
// } from 'recharts';
import RRIStationChart from './RRIStationChart';
import moment from 'moment';
import RRIColorScaleLegend from './RRIColorScaleLegend';
import dayjs from 'dayjs';

const tabConfig = [
    {
        label: 'น้ำไหลลงอ่าง',
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polyline
                    points="2,12 6,6 10,16 14,8 18,12 22,12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
            </svg>
        ),
    },
];

export default function RRIRightSidebar({ open, onClose, selected, waterlevelMeta, damMeta }) {
  const [activeTab, setActiveTab] = React.useState(0);
  const [chartData, setChartData] = React.useState([]);
  const [chartLoading, setChartLoading] = React.useState(false);
  const [csvData, setCsvData] = React.useState([]);
  const [alarmData, setAlarmData] = React.useState({ hr: null, qr: null });
  const chartRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const [latestObserved, setLatestObserved] = useState(null);
  const [latestObservedDate, setLatestObservedDate] = useState(null);
  const [reservoirUnit, setReservoirUnit] = useState('mcm');

  React.useEffect(() => {
    if (!selected?.name) return;
    setCsvData([]);
    const url = `${process.env.REACT_APP_LIVE_HII}/product/latest/rri/station/${selected.name}.csv`;
    fetch(url)
      .then(res => res.text())
      .then(text => {
        const lines = text.trim().split('\n');
        const parsed = lines.slice(1).map(line => {
          const cols = line.split(',');
          return {
            ts: moment(cols[0], 'YYYY-MM-DD HH:mm:ss').valueOf(),
            hr: parseFloat(cols[1]),
            qr: parseFloat(cols[2]),
            qrMCM: parseFloat(cols[3])
          };
        }).filter(d => !isNaN(d.ts));
        setCsvData(parsed);
      })
      .catch(() => setCsvData([]));
  }, [selected?.name]);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_LIVE_HII}/product/latest/rri/metadata/csv/rri_station_coordinate_hr.csv`)
      .then(res => res.text())
      .then(text => {
        const lines = text.trim().split('\n');
        const headers = lines[0].split(',');
        const found = lines.slice(1).find(line => {
          const cols = line.split(',');
          return cols[1] === selected?.name;
        });
        if (found) {
          const cols = found.split(',');
          setAlarmData(prev => ({ ...prev, hr: cols[7] || '-' }));
        }
      })
      .catch(() => {});

    fetch(`${process.env.REACT_APP_LIVE_HII}/product/latest/rri/metadata/csv/rri_station_coordinate_qr.csv`)
      .then(res => res.text())
      .then(text => {
        const lines = text.trim().split('\n');
        const found = lines.slice(1).find(line => {
          const cols = line.split(',');
          return cols[1] === selected?.name;
        });
        if (found) {
          const cols = found.split(',');
          setAlarmData(prev => ({ ...prev, qr: cols[6] || '-' }));
        }
      })
      .catch(() => {});
  }, [selected?.name]);

  useEffect(() => {
    if (open) {
      setTimeout(() => setChartReady(true), 300); // ให้ animation เสร็จก่อน
    } else {
      setChartReady(false);
    }
  }, [open]);

  const nowTs = moment().valueOf();

  useEffect(() => {
    if (!selected?.name) return;
    setLatestObserved(null);
    setLatestObservedDate(null);

    const today = dayjs();
    const startDate = today.subtract(7, 'day').format('YYYY-MM-DD');
    const endDate = today.format('YYYY-MM-DD HH:mm');

    if (selected.type === 'station') {
      const stationId = selected.id;
      if (!stationId) return;

      fetch(`${process.env.REACT_APP_API_URL}/public/waterlevel_graph?station_type=tele_waterlevel&station_id=${stationId}&start_date=${startDate}&end_date=${endDate}`)
        .then(res => res.json())
        .then(data => {
          const rows = data?.data?.graph_data;
          if (!rows || rows.length === 0) return;
          const last = [...rows].reverse().find(r => r.discharge !== null);
          setLatestObserved(last?.discharge ?? null);
          setLatestObservedDate(last?.datetime ?? null);
        })
        .catch(() => {});

    } else if (selected.type === 'reservoir') {
      const damId = selected.dam_id;
      if (!damId) return;

      const thisYear = today.year();
      const isMedium = damMeta?.[damId]?.isMedium ?? false;

      if (isMedium) {
        fetch(`${process.env.REACT_APP_API_URL}/analyst/dam_medium_graph?data_type=mediumdam_inflow&medium_id=${damId}&year=${thisYear}`)
          .then(res => res.json())
          .then(data => {
            const yearData = data?.data?.graph_data?.[0]?.data;
            if (!yearData || yearData.length === 0) return;
            const last = yearData.find(r => r.value !== null);
            setLatestObserved(last?.value ?? null);
            setLatestObservedDate(last?.date ?? null);
          })
          .catch(() => {});
      } else {
        fetch(`${process.env.REACT_APP_API_URL}/analyst/dam_yearly_graph?data_type=dam_inflow&dam_id=${damId}&year=${thisYear}`)
          .then(res => res.json())
          .then(data => {
            const yearData = data?.data?.graph_data?.[0]?.data;
            if (!yearData || yearData.length === 0) return;
            const last = [...yearData].reverse().find(r => r.value !== null);
            setLatestObserved(last?.value ?? null);
            setLatestObservedDate(last?.date ?? null);
          })
          .catch(() => {});
      }
    }
  }, [selected?.name, selected?.type]);

  const MCM_TO_CMS = 1000000 / 86400;

  const convertReservoir = (v) => {
    if (v === null || v === undefined || isNaN(v)) return null;
    return reservoirUnit === 'cms' ? v * MCM_TO_CMS : v;
  };

  const reservoirUnitLabel = reservoirUnit === 'cms' ? 'ลบ.ม. / วินาที' : 'ล้านลบ.ม / วัน';

  const latestValue = csvData.length > 0
    ? csvData.reduce((prev, curr) =>
        Math.abs(curr.ts - nowTs) < Math.abs(prev.ts - nowTs) ? curr : prev
      )
    : null;

  const latestDisplay = latestObserved !== null && latestObserved !== undefined
    ? Number(selected?.type === 'reservoir' ? convertReservoir(latestObserved) : latestObserved).toFixed(2)
    : '-';

  const maxDisplay = csvData.length > 0
    ? Math.max(...csvData.map(d =>
        selected?.type === 'reservoir' && reservoirUnit === 'mcm' ? d.qrMCM : d.qr
      )).toFixed(2)
    : '-';

  const latestDateStr = latestObservedDate
    ? selected?.type === 'reservoir'
      ? moment(latestObservedDate, 'YYYY-MM-DD').locale('th').format('DD MMM')
      : moment(latestObservedDate, 'YYYY-MM-DD HH:mm').locale('th').format('DD MMM · HH:mm')
    : '-';

  const maxField = selected?.type === 'reservoir' && reservoirUnit === 'mcm' ? 'qrMCM' : 'qr';

  const maxValue = csvData.length > 0
    ? csvData.reduce((prev, curr) => (curr[maxField] > prev[maxField] ? curr : prev))
    : null;

  const maxDateStr = maxValue
    ? moment(maxValue.ts).locale('th').format('DD MMM · HH:mm')
    : '-';

  const oldcode = selected?.type === 'station'
    ? waterlevelMeta?.[selected?.id]?.oldcode
    : null;

  const stationName =
    waterlevelMeta?.[selected?.id]?.station_name
    ?? selected?.name_th
    ?? selected?.name
    ?? '-';

  const alarmValue = activeTab === 0 ? parseFloat(alarmData.hr) : parseFloat(alarmData.qr);
  const field = activeTab === 0 ? 'hr' : 'qr';

  const now36h = moment().add(36, 'hours').valueOf();
  const next36Data = csvData.filter(d => d.ts > nowTs && d.ts <= now36h);

  const currentVal = latestValue ? latestValue[field] : null;
  const max36Val = next36Data.length > 0 ? Math.max(...next36Data.map(d => d[field])) : null;
  const end36Val = next36Data.length > 0 ? next36Data[next36Data.length - 1][field] : null;

  const isPhase2 = max36Val !== null && !isNaN(alarmValue) && max36Val >= alarmValue;
  const phase = isPhase2 ? 2 : 1;
  const phaseColor = phase === 2 ? '#E53935' : '#A87000';
  const phaseBgColor = phase === 2 ? '#FFEBEE' : '#FFFDE7';
  const phaseBorderColor = phase === 2 ? '#FF9C91' : '#F1D58D';
  const phaseTitle = phase === 2 ? 'ระดับเตือนภัย' : 'ระดับเฝ้าระวัง';
  const phaseBackgroundColor = phase === 2 ? '#F8B4AC' : '#F8E6AC';
  const phaseBackgroundCapacityColor = phase === 2 ? '#F8B4AC' : '#F8E9C5';
  const phaseTextColor = phase === 2 ? '#AC1A00' : '#AC6400';
  const reservoirName = (selected?.name_th ?? selected?.name ?? '-')
    .replace(/(อ่างเก็บน้ำ\s*)+/g, '')
    .trim();
  const chartTitle = selected?.type === 'reservoir'
    ? `อ่างเก็บน้ำ${reservoirName}`
    : `${oldcode ? `${oldcode} ` : ''}${stationName}`.trim();
  const chartSubtitle = selected?.province
    ? `ต.${selected.tambon ?? ''} อ.${selected.amphoe ?? ''} จ.${selected.province ?? ''}`
    : '';
  const NO_DATA = 'ไม่มีข้อมูล';
  let arrowIcon = '⮕';
  let arrowLabel = 'คงที่';
  let arrowColor = phaseTextColor;
  if (currentVal !== null && end36Val !== null) {
    const diff = end36Val - currentVal;
    if (diff > 1) { arrowIcon = '⬈'; arrowLabel = 'เพิ่มขึ้น'; arrowColor = '#E53935'; }
    else if (diff < -1) { arrowIcon = '⬊'; arrowLabel = 'ลดลง'; arrowColor = '#31985F'; }
  }

  const arrowRotate = 
  arrowLabel === 'เพิ่มขึ้น' ? '0deg' :
  arrowLabel === 'ลดลง' ? '90deg' : '45deg';

  const isValidNum = (v) => {
    if (v === null || v === undefined || v === '') return false;
    return Number.isFinite(Number(v));
  };

  const isValidText = (s) =>
    typeof s === 'string' && s.trim() !== '' && !/nan|invalid date/i.test(s);

  const getYesterdayDDMMYYYY = () => dayjs().subtract(1, 'day').format('DD/MM/YYYY');

  return (
    <div style={{
      width: open ? 430 : 0,
      minWidth: open ? 430 : 0,
      height: '100%',
      borderTopRightRadius: 16,
      borderBottomRightRadius: 16,
      backgroundColor: '#FFFFFF',
      overflowX: 'hidden',
      overflowY: 'auto',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.25s ease, min-width 0.25s ease',
    }}>
      {open && (
        <Box p={2} style={{ position: 'sticky', top: 0, backgroundColor: '#FFFFFF', zIndex: 10 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>            
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon style={{ fontSize: 18, color:'#000000' }} />
                </IconButton>              
                <Typography style={{ fontFamily: 'Noto Sans Thai', fontWeight: 700, fontSize: 20, marginTop: 8, color: "#172B3D" }}>
                    {chartTitle}
                </Typography>
            </Box>

          </Box>


          {/* Location */}
          {selected?.province && (
            <Box mb={1} display="flex" alignItems="center" style={{ gap: 4 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="16" viewBox="0 0 24 28" fill="none">
            <path
                d="M12 0C7.589 0 4 3.589 4 8c0 5.5 8 16 8 16s8-10.5 8-16c0-4.411-3.589-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"
                stroke="#6B7280"
                strokeWidth="1.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                fill="none"
            />
            </svg>
            <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 12, color: '#6B7280' }}>
                {chartSubtitle}
            </Typography>
            </Box>
          )}          
        </Box>
      )}

      <Divider
          style={{
              marginBottom: 20,
              marginLeft: -16,
              marginRight: -16,
              backgroundColor: '#DBE5EC',
              height: 1,
          }}
      />

      {/* <Box
        style={{
          width: '95%',
          borderRadius: 7,
          border: `1px solid ${phaseBorderColor}`,
          backgroundColor: phaseBgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          marginLeft: '18px',
        }}
      >
        Left: Icon + Title + Subtitle
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          Icon Box
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: 6,
              border: `1px solid ${phaseColor}`,
              backgroundColor: phaseBackgroundColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2L2 20h20L12 2z"
                stroke={phaseColor}
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <line x1="12" y1="9" x2="12" y2="14" stroke={phaseColor} strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1" fill={phaseColor} />
            </svg>
          </Box>

          Text
          <Box>
            <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 13, fontWeight: 700, color: '#172B3D' }}>
              {phaseTitle}
            </Typography>
            <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 10, color: '#776B4D' }}>
              คาดการณ์ระดับน้ำเพิ่มขึ้นต่อเนื่องใน 36 ชั่วโมง
            </Typography>
          </Box>
        </Box>

        Right: Arrow
        <Box display="flex" flexDirection="row" alignItems="center" style={{ gap: 4, width: 55, height: 34, borderRadius: 6, backgroundColor: phaseBackgroundCapacityColor, justifyContent: 'center' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none"
            style={{ transform: `rotate(${arrowRotate})`, transition: 'transform 0.3s ease' }}
          >
            <path stroke={phaseTextColor} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6.343 17.657L17.657 6.343m0 0v8.485m0-8.485H9.172"/>
          </svg>
          <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 10, color: phaseTextColor, marginTop: '1px' }}>
            {arrowLabel}
          </Typography>
        </Box>
      </Box> */}

      <Box
          style={{
              borderRadius: 16,
              border: '1px solid #D9E5EE',
              marginTop: 15,
              marginLeft: 15,
              marginBottom: 10,
              overflow: 'hidden',
              overflow: 'visible'
          }}
      >
          <Box display="flex">
              {/* ช่องที่ 1 */}
              <Box
                  style={{
                      width: selected?.type === 'reservoir' ? '50%' : '33.33%',
                      padding: '18px 14px 24px',
                      borderRight: '1px solid #D9E5EE',
                  }}
              >
                  {/* Title */}
                  <Typography
                      style={{
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 10,
                          color: '#7E8F9F',
                          marginBottom: 10,
                          height: 12,
                          lineHeight: '12px',
                      }}
                  >
                      ค่าตรวจวัดล่าสุด
                  </Typography>

                  {/* Value */}
                  <Typography
                      style={{
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 17,
                          fontWeight: 700,
                          lineHeight: '17px',
                          marginBottom: 10,
                          height: 17,
                          color: '#172B3D',
                      }}
                  >
                      {latestDisplay} {selected?.type === 'reservoir' ? reservoirUnitLabel : 'ลบ.ม. / วินาที'}
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                      style={{
                          marginTop: 20,
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 10,
                          color: '#8091A2',
                          lineHeight: '11px',
                          height: 11,
                      }}
                  >
                      {latestDateStr}
                  </Typography>
              </Box>

              {/* ช่องที่ 2 */}
              <Box
                  style={{
                      width: selected?.type === 'reservoir' ? '50%' : '33.33%',
                      padding: '18px 14px 24px',
                      borderRight: '1px solid #D9E5EE',
                  }}
              >
                  {/* Title */}
                  <Typography
                      style={{
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 10,
                          color: '#7E8F9F',
                          marginBottom: 10,
                          height: 12,
                          lineHeight: '12px',
                      }}
                  >
                      สูงสุดที่คาดการณ์
                  </Typography>

                  {/* Value */}
                  <Typography
                      style={{
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 17,
                          fontWeight: 700,
                          lineHeight: '17px',
                          marginBottom: 10,
                          height: 17,
                      }}
                  >
                    {isValidNum(maxDisplay)
                        ? `${maxDisplay} ${selected?.type === 'reservoir' ? reservoirUnitLabel : 'ลบ.ม. / วินาที'}`
                        : NO_DATA}
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                      style={{
                          marginTop: 20,
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 10,
                          color: '#8091A2',
                          lineHeight: '11px',
                          height: 11,
                      }}
                  >
                      {maxDateStr} 
                  </Typography>
              </Box>

              {/* ช่องที่ 3 */}
              {selected?.type !== 'reservoir' && (
              <Box
                  style={{
                      width: '33.33%',
                      padding: '18px 14px 24px',
                  }}
              >
                  {/* Title */}
                  <Typography
                      style={{
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 10,
                          color: '#7E8F9F',
                          marginBottom: 10,
                          height: 12,
                          lineHeight: '12px',
                      }}
                  >
                      ระดับเตือนภัย
                  </Typography>

                  {/* Value */}
                  <Typography
                      style={{
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 17,
                          fontWeight: 700,
                          lineHeight: '17px',
                          marginBottom: 10,
                          height: 17,
                          // whiteSpace: 'nowrap',
                      }}
                  >
                    {alarmData.qr ?? '-'} {selected?.type === 'reservoir' ? reservoirUnitLabel : 'ลบ.ม. / วินาที'}
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                      style={{
                          marginTop: 20,
                          fontFamily: 'Noto Sans Thai',
                          fontSize: 10,
                          color: '#8091A2',
                          lineHeight: '11px',
                          height: 11,
                      }}
                  >
                      เกณฑ์ประจำสถานี
                  </Typography>
              </Box>
              )}
          </Box>
      </Box>

      <Box style={{
        width: 150,
        height: 42,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: '8px 4px',
        borderBottom: '2px solid #0878C9',
        marginTop: 16, marginLeft: 18
      }}>
        {selected?.type === 'reservoir' ? (
          <WavesIcon style={{ fontSize: 20, color: '#0878C9' }} />
        ) : (
          <svg
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            width="20"
            height="20"
          >
            <path
              stroke="#0878C9"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 12h4l3 7 4-14 3 7h4"
            />
          </svg>
        )}
        <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 13, fontWeight: 700, color: '#0878C9' }}>
          {selected?.type === 'reservoir' ? 'ปริมาณน้ำไหลลงอ่าง' : 'ปริมาณน้ำท่า'}
        </Typography>
      </Box>

      <Box style={{ marginTop: 16, marginLeft: 18 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" style={{ paddingTop: 12, paddingRight: 16 }}>
              <Box>
                  <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 13, fontWeight: 700, color: '#172B3D' }}>
                      {selected?.type === 'reservoir' ? 'กราฟปริมาณน้ำไหลลงอ่าง' : 'กราฟปริมาณน้ำท่า'}
                  </Typography>
                  <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 10, color: '#7E8F9F' }}>
                      {selected?.type === 'reservoir'
                        ? `ค่าตรวจวัดรายวัน · ${reservoirUnit === 'cms' ? 'ลูกบาศก์เมตร / วินาที' : 'ล้านลูกบาศก์เมตร / วัน'}`
                        : 'ค่าตรวจวัดรายชั่วโมง · ลูกบาศก์เมตร / วินาที'}
                  </Typography>
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                {selected?.type === 'reservoir' && (
                  <Tooltip
                    title={
                      reservoirUnit === 'cms'
                        ? 'เปลี่ยนหน่วยเป็น ล้าน ลบ.ม. / วัน'
                        : 'เปลี่ยนหน่วยเป็น ลบ.ม. / วินาที'
                    }
                    PopperProps={{ style: { zIndex: 99999 } }}
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <Box
                        onClick={() => setReservoirUnit(prev => prev === 'mcm' ? 'cms' : 'mcm')}
                        style={{
                          height: 27,
                          padding: '0 10px',
                          borderRadius: 5,
                          border: '1px solid #D9E4EB',
                          background: reservoirUnit === 'cms' ? '#0878C9' : '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography
                          style={{
                            fontFamily: 'Noto Sans Thai',
                            fontSize: 11,
                            fontWeight: 700,
                            color: reservoirUnit === 'cms' ? '#FFFFFF' : '#172B3D',
                          }}
                        >
                          {reservoirUnit === 'cms' ? 'ลบ.ม. / วินาที' : 'ล้าน ลบ.ม. / วัน'}
                        </Typography>
                      </Box>
                    </span>
                  </Tooltip>
                )}
              <Box
                style={{ width: 27, height: 27, borderRadius: 5, border: '1px solid #D9E4EB', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}
                onClick={() => setMenuOpen(prev => !prev)}
              >
                <Typography style={{ fontSize: 18, color: '#020202', letterSpacing: 1, lineHeight: 1 }}>···</Typography>
                {menuOpen && (
                  <Box style={{
                    position: 'absolute', top: 30, right: 0, zIndex: 9999,
                    backgroundColor: '#fff', border: '1px solid #D9E4EB',
                    borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    minWidth: 160, overflow: 'hidden',
                  }}>
                    {[
                      { label: 'Reset Zoom', action: () => chartRef.current?.chart?.zoom() },
                      { label: 'ดาวน์โหลด PNG', action: () => chartRef.current?.chart?.exportChart({ type: 'image/png' }) },
                      { label: 'ดาวน์โหลด JPEG', action: () => chartRef.current?.chart?.exportChart({ type: 'image/jpeg' }) },
                      { label: 'ดาวน์โหลด PDF', action: () => chartRef.current?.chart?.exportChart({ type: 'application/pdf' }) },
                      { label: 'ดาวน์โหลด SVG', action: () => chartRef.current?.chart?.exportChart({ type: 'image/svg+xml' }) },
                      { label: 'ดาวน์โหลด CSV', action: () => chartRef.current?.chart?.downloadCSV() },
                      { label: 'ดาวน์โหลด XLS', action: () => chartRef.current?.chart?.downloadXLS() },
                    ].map(item => (
                      <Box
                        key={item.label}
                        onClick={(e) => { e.stopPropagation(); item.action(); setMenuOpen(false); }}
                        style={{ padding: '10px 16px', cursor: 'pointer', fontFamily: 'Noto Sans Thai', fontSize: 12, color: '#172B3D' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F5F8FA'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {item.label}
                      </Box>
                    ))}
                  </Box>
                )}
                </Box>
              </Box>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ paddingTop: 12, gap: 16, paddingRight: 16 }}>
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="5" fill="#000000" /></svg>
                  <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 10, color: '#7E8F9F' }}>{selected?.type === 'reservoir' && reservoirUnit === 'mcm' ? 'ค่าตรวจวัดรายวัน' : 'ค่าตรวจวัดรายชั่วโมง'}</Typography>
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12">
                      <circle
                        cx="6" cy="6" r="5"
                        fill={selected?.type === 'reservoir' ? '#fcefed' : '#ecf5f0'}
                        stroke={selected?.type === 'reservoir' ? '#df5745' : '#31985f'}
                        strokeWidth="2"
                      />
                    </svg>
                  <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 10, color: '#7E8F9F' }}>{selected?.type === 'reservoir' && reservoirUnit === 'mcm' ? 'ค่าคาดการณ์รายวัน' : 'ค่าคาดการณ์รายชั่วโมง'}</Typography>
              </Box>
              {/* <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="5" fill="#FF0000" /></svg>
                  <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 10, color: '#7E8F9F' }}>เกณฑ์เตือนภัย</Typography>
              </Box> */}
          </Box>

          <Box style={{ paddingTop: 16 }}>
            {chartReady && (
              <RRIStationChart ref={chartRef} stationName={selected?.name} type={selected?.type} stationId={selected?.id} damId={selected?.dam_id} isMedium={damMeta?.[selected?.dam_id]?.isMedium ?? false} chartTitle={chartTitle} chartSubtitle={chartSubtitle} reservoirUnit={reservoirUnit}/>
            )}
            <Box>
              <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 10, color: '#7E8F9F' }}><span style={{ fontWeight: 'bold' }}>หมายเหตุ </span>: ข้อมูลคาดการณ์รายชั่วโมง ประมวลผล ณ วันที่ {getYesterdayDDMMYYYY()} เวลา 07.00 น</Typography>
            </Box>
          </Box>

          {selected?.type !== 'reservoir' && (
            <>
              <Divider style={{ marginTop: 20, marginBottom: 20, marginLeft: -16, marginRight: -16, backgroundColor: '#DBE5EC', height: 1 }} />
              <RRIColorScaleLegend />
            </>
          )}
      </Box>

      <Divider
          style={{
              marginTop: 20,
              marginBottom: 20,
              marginLeft: -16,
              marginRight: -16,
              backgroundColor: '#DBE5EC',
              height: 1,
          }}
      />
      <Box style={{ marginLeft: 18, marginRight: 16, }}>
        <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 10, color: '#7E8F9F' }}>
          ข้อมูลสถานี
        </Typography>
        <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 13, fontWeight: 700, color: '#172B3D', marginBottom: 12 }}>
          รายละเอียดและแหล่งที่มา
        </Typography>

        <Divider style={{ backgroundColor: '#DBE5EC' }} />
          <Box display="flex" alignItems="stretch" style={{ gap: 8 }}>
            <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 12, color: '#4A5568', padding: '10px 0', flex: 1 }}>
              แม่น้ำ <span style={{color : "#172B3D"}}>{selected?.STR_NAMT ?? '-'}</span>
            </Typography>
            <Box style={{ width: 1, backgroundColor: '#DBE5EC' }} />
            <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 12, color: '#4A5568', padding: '10px 0', flex: 1 }}>
              หน่วยงาน <span style={{color : "#172B3D"}}>{selected?.Agency ?? '-'}</span>
            </Typography>
          </Box>
        <Divider style={{ backgroundColor: '#DBE5EC' }} />
      </Box>

      <Divider
          style={{
              marginTop: 20,
              marginBottom: 20,
              marginLeft: -16,
              marginRight: -16,
              backgroundColor: '#DBE5EC',
              height: 1,
          }}
      />
      
    </div>
  );
}